import * as z from 'zod/mini';
import { DATA_FORMAT_VERSION, EXPORT_FORMAT, EXPORT_VERSION } from '../model/constants';
import { envelopeSchema } from '../model/envelope-schema';
import type { AnyEnvelope } from '../model/types';

export interface ExportFile {
  readonly format: typeof EXPORT_FORMAT;
  readonly version: number;
  /** Data-format version of the exporting device (data dictionary §8, §10); absent = 1. */
  readonly dataFormat: number;
  readonly exportedAt: string;
  readonly records: readonly AnyEnvelope[];
}

export const ExportErrorReason = {
  InvalidJson: 'invalidJson',
  InvalidFormat: 'invalidFormat',
  UnsupportedVersion: 'unsupportedVersion',
  /** Written by a newer build whose data format this build cannot read. */
  NewerDataFormat: 'newerDataFormat',
  InvalidRecord: 'invalidRecord',
} as const;
export type ExportErrorReason = (typeof ExportErrorReason)[keyof typeof ExportErrorReason];

export class ExportFormatError extends Error {
  readonly reason: ExportErrorReason;
  constructor(reason: ExportErrorReason, message: string) {
    super(message);
    this.name = 'ExportFormatError';
    this.reason = reason;
  }
}

type ExportMigration = (raw: Record<string, unknown>) => Record<string, unknown>;

/**
 * Migrations from an older export version to the next one, keyed by the version they upgrade
 * from. Version 1 is the first version, so the table is empty; add an entry when the format changes.
 */
export const EXPORT_MIGRATIONS: Readonly<
  Record<number, (raw: Record<string, unknown>) => Record<string, unknown>>
> = {};

const exportHeaderSchema = z.object({
  format: z.literal(EXPORT_FORMAT),
  version: z.int().check(z.minimum(1)),
  dataFormat: z.optional(z.int().check(z.minimum(1))),
  exportedAt: z.string().check(z.minLength(1)),
  records: z.array(z.unknown()),
});

/** Full export package of the given records (sorted by id for reproducible output). */
export function buildExport(records: readonly AnyEnvelope[], nowIso: string): ExportFile {
  const sorted = [...records].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  return {
    format: EXPORT_FORMAT,
    version: EXPORT_VERSION,
    dataFormat: DATA_FORMAT_VERSION,
    exportedAt: nowIso,
    records: sorted,
  };
}

/** Upgrade a raw export package from version `from` to `to` by applying the migrations in order. */
export function migrateExport(
  raw: Record<string, unknown>,
  from: number,
  to: number = EXPORT_VERSION,
  migrations: Readonly<Record<number, ExportMigration>> = EXPORT_MIGRATIONS,
): Record<string, unknown> {
  let current = raw;
  for (let version = from; version < to; version += 1) {
    const step = migrations[version];
    if (step === undefined) {
      throw new ExportFormatError(
        ExportErrorReason.UnsupportedVersion,
        `No migration from export version ${version}`,
      );
    }
    current = { ...step(current), version: version + 1 };
  }
  return current;
}

/** Parse and validate an export package from JSON text or an already parsed value. */
export function parseExport(json: unknown): ExportFile {
  let value: unknown = json;
  if (typeof json === 'string') {
    try {
      value = JSON.parse(json);
    } catch {
      throw new ExportFormatError(ExportErrorReason.InvalidJson, 'The file is not valid JSON');
    }
  }
  const header = exportHeaderSchema.safeParse(value);
  if (!header.success) {
    throw new ExportFormatError(
      ExportErrorReason.InvalidFormat,
      `Not a WizTime export: ${header.error.issues.map((issue) => issue.message).join('; ')}`,
    );
  }
  if (header.data.version > EXPORT_VERSION) {
    throw new ExportFormatError(
      ExportErrorReason.UnsupportedVersion,
      `Export version ${header.data.version} is newer than supported version ${EXPORT_VERSION}`,
    );
  }
  const dataFormat = header.data.dataFormat ?? 1;
  if (dataFormat > DATA_FORMAT_VERSION) {
    throw new ExportFormatError(
      ExportErrorReason.NewerDataFormat,
      `Data format ${dataFormat} is newer than supported format ${DATA_FORMAT_VERSION}`,
    );
  }
  const migrated = migrateExport(header.data, header.data.version);
  const rawRecords = migrated['records'];
  const records: AnyEnvelope[] = [];
  (Array.isArray(rawRecords) ? rawRecords : []).forEach((rawRecord: unknown, index: number) => {
    const parsed = envelopeSchema.safeParse(rawRecord);
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      throw new ExportFormatError(
        ExportErrorReason.InvalidRecord,
        `Record ${index} is invalid: ${issue?.path.join('.') ?? ''} ${issue?.message ?? ''}`.trim(),
      );
    }
    records.push(parsed.data);
  });
  return {
    format: EXPORT_FORMAT,
    version: EXPORT_VERSION,
    dataFormat,
    exportedAt: header.data.exportedAt,
    records,
  };
}
